/* eslint-disable no-unused-vars */
import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import { setLineSaveMutation, setLineUpdateMutation } from '@/utils/helpers/DataEntryViews/SetMutationType'
import { LINES_HAVE_BEEN_SAVED, CLEAR_STATE } from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'

const lineSchema = new schema.Entity('lines')
const lineListSchema = new schema.Array(lineSchema)

const state = {
  linesSaved: false
}

const actions = {
  saveLine (store, payload) {
    const { type, id, line } = payload
    let { receiveMutation } = setLineSaveMutation(type)
    if (!payload) return Promise.reject('Error saving lines')
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.post(buildUrlWithQuery(`${type}/${id}/lines`, null, instanceUrl), line, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r && r.id) {
          const { entities } = normalize(r, lineSchema)
          this.commit(receiveMutation, { id, data: entities.lines })
        }
        return { response: r, line: payload.line }
      })
      .catch(e => {
        console.log(e)
        return { response: e, line: payload.line }
      })
  },
  updateLine (store, payload) {
    const { type, id, line } = payload
    let { receiveMutation, updateMutation, updateFailMutation } = setLineUpdateMutation(type)
    if (!payload) return this.commit(updateFailMutation, 'Error saving lines')
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(updateMutation, { type, status: true })
    return http.patch(buildUrlWithQuery(`lines/${type}/${id}`, null, instanceUrl), line, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(updateMutation, false)
        if (r) {
          const { entities, result } = normalize(r, lineSchema)
          if (!result) {
            this.commit(updateFailMutation, 'Error normalizing response')
            return r
          }
          this.commit(receiveMutation, { id, data: entities.lines })
          return r
        }
        return Promise.reject('No response')
      })
      .catch(e => {
        console.log(e)
        this.commit(updateFailMutation, handleXHRError(e))
        return e
      })
  },
  updateTransactionLine (store, payload) {
    const { type, id } = payload
    const lines = payload.data
    let { receiveMutation, updateMutation, updateFailMutation } = setLineUpdateMutation(type)
    if (!payload) return this.commit(updateFailMutation, 'Error saving lines')
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(updateMutation, { type, status: true })
    return http.post(buildUrlWithQuery(`${type}s/${id}/lines/bulk`, null, instanceUrl), lines, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(updateMutation, false)
        if (r) {
          const { entities, result } = normalize(r, lineSchema)
          if (!result) {
            this.commit(updateFailMutation, 'Error normalizing response')
            return r
          }
          this.commit(receiveMutation, { id, data: entities.lines })
          return r
        }
        return Promise.reject('No response')
      })
      .catch(e => {
        console.log(e)
        this.commit(updateFailMutation, handleXHRError(e))
        return e
      })
  },
  async fetchLinesById (store, payload) {
    const { type, id } = payload
    const bigType = type.toUpperCase()
    let receiveMutation = `RECEIVE_${bigType}_LINES`
    let receivingMutation = `RECEIVING_${bigType}_LINES`
    const url = buildUrlWithQuery(`lines/${type}/${id}`, null, this.getters.getActiveInstance.url)
    try {
      this.commit(receivingMutation, true)
      const resp = await http.get(url, { headers: getHeaders(this.getters.getToken) })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(receivingMutation, false)
      const { entities } = normalize(resp, lineSchema)
      this.commit(receiveMutation, { id: null, data: entities.lines })
      return { type, query: null, count: resp.count, entities }
    } catch (e) {
      console.log(e)
    }
  },
  async fetchLinesByTransactionId (store, { type, id, query = null, pageSize = 20 }) {
    const bigType = type.toUpperCase()
    let receiveMutation = `RECEIVE_${bigType}_LINES`
    let receivingMutation = `RECEIVING_${bigType}_LINES`
    const url = buildUrlWithQuery(`${type}s/${id}/lines`, { page_size: pageSize }, this.getters.getActiveInstance.url)
    try {
      this.commit(receivingMutation, true)
      const firstPage = await http.get(url, { headers: getHeaders(this.getters.getToken) })
        .then(checkStatus)
        .then(parseJSON)
      if (!(firstPage && firstPage.results)) {
        throw new Error(`Error fetching lines for ${type} #${id}`)
      }
      let requests = []
      if (firstPage.count > pageSize) {
        const pageCt = Math.ceil(firstPage.count / pageSize)
        for (let i = 2; i <= pageCt; i++) {
          requests.push(http.get(`${url}&page=${i}`, { headers: getHeaders(this.getters.getToken) })
            .then(checkStatus)
            .then(parseJSON)
          )
        }
      }
      const nextPages = await Promise.all(requests)
      let allResults = firstPage.results
      if (nextPages && nextPages.length) {
        allResults = [...allResults, ...nextPages.reduce((a, b) => a.concat(b.results), [])]
      }
      this.commit(receivingMutation, false)
      const { entities } = normalize(allResults, lineListSchema)
      this.commit(receiveMutation, { id: null, data: entities.lines })
      return { type, query, count: firstPage.count, entities }
    } catch (e) {
      console.log(e)
      this.commit(receivingMutation, false)
      return e
    }
  },
  async fetchLinesWithQuery (store, { type, query = null, pageSize = 200 }) {
    const bigType = type.toUpperCase()
    let receiveMutation = `RECEIVE_${bigType}_LINES`
    let receivingMutation = `RECEIVING_${bigType}_LINES`
    if (!query.page_size) query.page_size = pageSize
    const url = buildUrlWithQuery(`lines/${type}`, query, this.getters.getActiveInstance.url)
    try {
      this.commit(receivingMutation, true)
      const firstPage = await http.get(url, { headers: getHeaders(this.getters.getToken) })
        .then(checkStatus)
        .then(parseJSON)
      if (!(firstPage && firstPage.results)) throw new Error(`Error fetching ${type} lines`)
      let requests = []
      if (firstPage.count > pageSize) {
        const pageCt = Math.ceil(firstPage.count / pageSize)
        for (let i = 2; i <= pageCt; i++) {
          requests.push(http.get(`${url}&page=${i}`, { headers: getHeaders(this.getters.getToken) })
            .then(checkStatus)
            .then(parseJSON)
          )
        }
      }
      const nextPages = await Promise.all(requests)
      let allResults = firstPage.results
      if (nextPages && nextPages.length) {
        allResults = [...allResults, ...nextPages.reduce((a, b) => a.concat(b.results), [])]
      }
      this.commit(receivingMutation, false)
      const { entities } = normalize(allResults, lineListSchema)
      this.commit(receiveMutation, { id: null, data: entities.lines })
      return { type, query, count: firstPage.count, entities }
    } catch (e) {
      console.log(e)
      this.commit(receivingMutation, false)
      return e
    }
  },
  deleteLine (store, payload) {
    const { type, id } = payload
    const bigType = type.toUpperCase()
    let deleteMutation = `DELETE_${bigType}_LINES`
    const instanceUrl = this.getters.getActiveInstance.url
    return http.delete(buildUrlWithQuery(`lines/${type}/${id}`, null, instanceUrl), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(response => {
        if (response && response.ok) this.commit(deleteMutation, id)
        return { deletedLine: id, response }
      })
      .catch(e => {
        console.log(e)
        return { deletedLine: id, response: e }
      })
  },
  linesHaveBeenSaved (store, payload) {
    this.commit(LINES_HAVE_BEEN_SAVED, payload)
  }
}

const getters = {
  getLineSaveStatus: (state) => {
    return state.linesSaved
  },
  getLineById: (state, rootState) => (entityType, lineId) => {
    const lines = rootState[`get${entityType}Lines`]
    if (lines && Object.keys(lines).length) {
      if (Array.isArray(lines)) {
        return lines.find(line => {
          if (line.id) {
            return parseInt(line.id) === parseInt(lineId)
          }
        })
      } else {
        return lines[lineId] || {}
      }
    }
    return {}
  }
}

const mutations = {
  [LINES_HAVE_BEEN_SAVED] (state, payload) {
    state.linesSaved = payload
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
